import { useEffect, useState, SyntheticEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Skeleton,
  Chip,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import {
  useAfiliado,
  useCreateAfiliado,
  useUpdateAfiliado,
  useCrearAfiliadoEmpresa,
} from '../../hooks/useAfiliados';
import { useAuth } from '../../context/AuthContext';
import { mapSupabaseError } from '../../utils/errores';
import {
  afiliadoFormSchema,
  defaultAfiliadoValues,
  type AfiliadoFormValues,
} from './afiliadoSchema';
import {
  ESTADO_AFILIADO_LABELS,
  ESTADO_AFILIADO_COLORS,
  type EstadoAfiliado,
  type TipoDocumento,
  type SexoTipo,
  type EstadoCivil,
} from '../../types/afiliado';
import TabDatosPersonales from './tabs/TabDatosPersonales';
import TabDomicilio from './tabs/TabDomicilio';
import TabAfiliacion from './tabs/TabAfiliacion';
import TabGrupoFamiliar from './tabs/TabGrupoFamiliar';
import TabAportesExtra from './tabs/TabAportesExtra';
import TabDatosEmpleo from './tabs/TabDatosEmpleo';
import { formatCuit, todayLocal } from '../../utils/format';

type TabKey = 'datos' | 'domicilio' | 'afiliacion' | 'empleo' | 'aportes' | 'familia';

export default function AfiliadoForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nuevo';
  const navigate = useNavigate();

  const { effectiveEmpresaId, profile } = useAuth();
  const esEmpresa = profile?.rol === 'admin_empresa';
  // En contexto de empresa, el detalle se acota a afiliados de esa empresa; si
  // no corresponde, `afiliado` viene null y mostramos "sin acceso" (#494 CA-02).
  const { data: afiliado, isLoading: loadingAfiliado } = useAfiliado(
    isEdit ? id : undefined,
    effectiveEmpresaId,
  );
  const createMutation = useCreateAfiliado();
  const updateMutation = useUpdateAfiliado();
  const crearEmpresaMutation = useCrearAfiliadoEmpresa();

  const [activeTab, setActiveTab] = useState<TabKey>('datos');

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<AfiliadoFormValues>({
    resolver: zodResolver(afiliadoFormSchema),
    defaultValues: defaultAfiliadoValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isEdit && afiliado) {
      reset({
        cuil: afiliado.cuil,
        tipo_documento: afiliado.tipo_documento,
        numero_documento: afiliado.numero_documento,
        apellido: afiliado.apellido,
        nombre: afiliado.nombre,
        sexo: afiliado.sexo ?? undefined,
        fecha_nacimiento: afiliado.fecha_nacimiento ?? '',
        estado_civil: afiliado.estado_civil ?? undefined,
        domicilio: afiliado.domicilio ?? '',
        provincia_id: afiliado.provincia_id,
        localidad_id: afiliado.localidad_id,
        telefono: afiliado.telefono ?? '',
        email: afiliado.email ?? '',
        fecha_alta_sistema: afiliado.fecha_alta_sistema ?? '',
        estado: afiliado.estado,
        observaciones: afiliado.observaciones ?? '',
      });
    }
  }, [afiliado, isEdit, reset]);

  const onSubmit = async (values: AfiliadoFormValues) => {
    const payload = {
      cuil: values.cuil,
      tipo_documento: values.tipo_documento as TipoDocumento,
      numero_documento: values.numero_documento,
      apellido: values.apellido,
      nombre: values.nombre,
      sexo: (values.sexo ?? null) as SexoTipo | null,
      fecha_nacimiento: values.fecha_nacimiento || null,
      estado_civil: (values.estado_civil ?? null) as EstadoCivil | null,
      domicilio: values.domicilio || null,
      provincia_id: values.provincia_id ?? null,
      localidad_id: values.localidad_id ?? null,
      telefono: values.telefono || null,
      email: values.email || null,
      fecha_alta_sistema:
        values.fecha_alta_sistema || todayLocal(),
      estado: values.estado as EstadoAfiliado,
      observaciones: values.observaciones || null,
    };

    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, payload });
      } else if (esEmpresa) {
        // Alta por empresa (#495): el RPC crea (o vincula por CUIL) y asocia el
        // afiliado a su empresa. La empresa solo crea -> vuelve al listado.
        await crearEmpresaMutation.mutateAsync(payload);
        navigate('/afiliados');
        return;
      } else {
        const created = await createMutation.mutateAsync(payload);
        // En alta admin, ir al detalle para poder cargar familia/aportes
        navigate(`/afiliados/${created.id}/editar`, { replace: true });
        return;
      }
      navigate('/afiliados');
    } catch {
      // El error se muestra abajo
    }
  };

  const mutation = isEdit
    ? updateMutation
    : esEmpresa
      ? crearEmpresaMutation
      : createMutation;
  const apiError = mutation.error as Error | null;

  const handleTabChange = (_e: SyntheticEvent, value: TabKey) => setActiveTab(value);

  // Funcion para detectar tabs con errores y poner badge visual
  const tabHasError = (fields: (keyof AfiliadoFormValues)[]): boolean =>
    fields.some((f) => !!errors[f]);

  const errDatos = tabHasError([
    'cuil', 'tipo_documento', 'numero_documento', 'apellido', 'nombre',
    'sexo', 'fecha_nacimiento', 'estado_civil', 'estado', 'observaciones',
  ]);
  const errDomicilio = tabHasError([
    'domicilio', 'provincia_id', 'localidad_id', 'telefono', 'email',
  ]);
  const errAfiliacion = tabHasError(['fecha_alta_sistema']);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/afiliados')}
          variant="text"
        >
          Volver
        </Button>
        <Box sx={{ ml: { sm: 2 } }}>
          <Typography variant="h5" fontWeight={700}>
            {isEdit
              ? afiliado
                ? `${afiliado.apellido}, ${afiliado.nombre}`
                : 'Cargando...'
              : 'Nuevo afiliado'}
          </Typography>
          {isEdit && afiliado && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: 'monospace' }}
              >
                CUIL {formatCuit(afiliado.cuil)}
              </Typography>
              <Chip
                label={ESTADO_AFILIADO_LABELS[afiliado.estado]}
                size="small"
                color={ESTADO_AFILIADO_COLORS[afiliado.estado]}
              />
            </Stack>
          )}
        </Box>
      </Stack>

      {loadingAfiliado && isEdit && (
        <Card sx={{ p: 3 }}>
          <Stack spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Stack>
        </Card>
      )}

      {isEdit && !loadingAfiliado && !afiliado && (
        <Card sx={{ p: 3 }}>
          <Alert severity="warning">
            No encontramos este afiliado o no tenés acceso a él en el contexto
            actual.
          </Alert>
        </Card>
      )}

      {(!isEdit || (!loadingAfiliado && !!afiliado)) && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Card>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="datos"
                label="Datos personales"
                sx={errDatos ? { color: 'error.main' } : undefined}
              />
              <Tab
                value="domicilio"
                label="Domicilio y contacto"
                sx={errDomicilio ? { color: 'error.main' } : undefined}
              />
              <Tab
                value="afiliacion"
                label="Afiliacion"
                sx={errAfiliacion ? { color: 'error.main' } : undefined}
              />
              <Tab value="empleo" label="Datos empleo" disabled={!isEdit} />
              <Tab value="aportes" label="Aportes Extra" disabled={!isEdit} />
              <Tab value="familia" label="Grupo familiar" disabled={!isEdit} />
            </Tabs>

            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              {apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {mapSupabaseError(apiError, 'No se pudo guardar el afiliado. Volvé a intentar; si persiste, avisá al administrador.')}
                </Alert>
              )}

              {!isEdit && activeTab !== 'datos' && activeTab !== 'domicilio' && activeTab !== 'afiliacion' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Primero guarda los datos basicos del afiliado para poder cargar
                  esta seccion.
                </Alert>
              )}

              {activeTab === 'datos' && (
                <TabDatosPersonales
                  control={control}
                  register={register}
                  errors={errors}
                  isEdit={isEdit}
                  setValue={setValue}
                  getValues={getValues}
                />
              )}
              {activeTab === 'domicilio' && (
                <TabDomicilio
                  control={control}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                />
              )}
              {activeTab === 'afiliacion' && (
                <TabAfiliacion register={register} errors={errors} />
              )}
              {activeTab === 'empleo' && isEdit && id && (
                <TabDatosEmpleo afiliadoId={id} />
              )}
              {activeTab === 'aportes' && isEdit && id && (
                <TabAportesExtra afiliadoId={id} />
              )}
              {activeTab === 'familia' && isEdit && id && (
                <TabGrupoFamiliar afiliadoId={id} />
              )}

              {/* Botones de guardado solo para los tabs del afiliado mismo */}
              {(activeTab === 'datos' ||
                activeTab === 'domicilio' ||
                activeTab === 'afiliacion') && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Button onClick={() => navigate('/afiliados')} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Save />
                      )
                    }
                    disabled={isSubmitting || (isEdit && !isDirty)}
                  >
                    {isSubmitting
                      ? 'Guardando...'
                      : isEdit
                      ? 'Guardar cambios'
                      : 'Crear afiliado'}
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </form>
      )}
    </Box>
  );
}
